import React, { useEffect, useState, forwardRef, useMemo } from "react";
import { Accordion, Button, Card, Dropdown } from "react-bootstrap";
import "./OpalInventory.css";
import TauriLoader2 from "../../../TauriLoader2/TauriLoader2";
import TauriImage from "../../../TauriImage/TauriImage";
import OpalAddItemModal from "./OpalAddItemModal/OpalAddItemModal";
import OpalInventoryFindModal from "./OpalInventoryFindModal/OpalInventoryFindModal";
import DatePicker from "react-datepicker";
import { addRecord, deleteRecord, deleteRecordByField, getAll, getNextId, getRecordById, updateRecord, upsertRecordsBySessionId } from "../../../../localDB";
import { useBaseProvider } from "../../../../BaseManager/BaseManager";
import OpalAlertModal from "../../../OpalAlertModal/OpalAlertModal";
import appURL from '../../../../appURL.json';
import { transformSessionInventory } from "../../CoverageFactoryData/CoverageFactoryData";
import { SessionItemAvailability, statusMapping, SyncStatusMap } from "../../../../visualizations/visualizations";
import { createPortal } from "react-dom";

const OpalInventory = ({ section, id }) => {
    const [data, setData] = useState([]);
    const [loading, setIsLoading] = useState(false);
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [checkedRows, setCheckedRows] = useState({});
    const [showItemModal, setShowItemModal] = useState(false);
    const [showInventoryItemModal, setShowInventoryItemModal] = useState(false);
    const [session, setSession] = useState(null);
    const [item, setItem] = useState([]);
    const [search, setSearch] = useState('');
    const [showValidation, setShowValidation] = useState(false);
    const [validationMessage, setValidationMessage] = useState('');
    const [invData, setInvData] = useState([]);
    const [units, setUnits] = useState([]);
    const [openDropdownRow, setOpenDropdownRow] = useState(null);

    const availabilityOptions = Object.keys(SessionItemAvailability);

    const getAvailabilityLabel = (value) =>
        availabilityOptions.find(
            key => SessionItemAvailability[key] === value
        ) || "Carried";

    const ReadOnlyInput = forwardRef(({ value, onClick }, ref) => (
        <input
            className="form-control mx-1"
            onClick={onClick}
            value={value}
            readOnly
            ref={ref}
        />
    ));

    const { isSync } = useBaseProvider();

    const [batchValues, setBatchValues] = useState({
        Distributor: "",
        ExpiryDate: "",
        StockRoomOnHand: 0,
        ShelfOnHand: 0
    });

    const handleAddSelected = async (selectedItems) => {
        if (session?.Status !== statusMapping.inprogress || isSync) return;

        if (!selectedItems.length) return;
        const existingItemIds = new Set(data.map(i => i.ItemID));

        for (const item of selectedItems) {
            if (existingItemIds.has(item.ID)) continue;

            try {
                const newId = await getNextId("SessionInventory");

                const newItem = {
                    ID: newId,
                    ItemID: item.ID,
                    UnitID: item.UnitID,
                    Minimum: 0,
                    StockRoomOnHand: 0,
                    ShelfOnHand: 0,
                    SessionID: id,
                    Remarks: "",
                    Requested: 0,
                    Executed: 0,
                    Lot: "",
                    Distributor: "",
                    Availability: SessionItemAvailability.Carried,
                    SyncStatus: SyncStatusMap.create
                };

                await addRecord('SessionInventory', newItem);
            } catch (error) {
                console.error(`Failed to add item`);
            }
        }

        fetchData();
    };

    const reset = () => {
        setIsAllChecked(false);
        setCheckedRows({});
        setSearch('');
        setValidationMessage('');
    };

    useEffect(() => {
        const fetchInitialData = async () => {
            await fetchOnlineData();
            await fetchData();
        };

        reset();
        fetchInitialData();
    }, [id]);

    useEffect(() => {
        const handleRefresh = async () => {
            await fetchOnlineData();
            await fetchData();
        };

        window.addEventListener("refreshSession", handleRefresh);

        return () => {
            window.removeEventListener("refreshSession", handleRefresh);
        };
    }, [id]);

    const fetchOnlineData = async () => {
        try {
            setIsLoading(true);

            const isOnline = JSON.parse(localStorage.getItem("isOnline"));
            const hasInternet = navigator.onLine;

            if (isOnline && hasInternet) {
                const token = localStorage.getItem("token");
                const devId = localStorage.getItem("devId");

                const response = await fetch(`${appURL.API_CoverageInventory}/${id}/inventory`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                        "X-ETR-DeviceID": devId
                    },
                });

                if (!response.ok) {
                    setValidationMessage("Failed to load inventory!");
                    setShowValidation(true);
                    return;
                }

                const data = await response.json();
                const transformData = await transformSessionInventory(data?.items || [], id);
                await upsertRecordsBySessionId('SessionInventory', id, transformData);
            }
        } catch (error) {
            setValidationMessage(error);
            setShowValidation(true);
        } finally {
            setIsLoading(false);
        }
    }


    const fetchData = async () => {
        setIsLoading(true);
        try {
            const ldata = await getAll('SessionInventory');
            const sess = await getRecordById('session', id);
            const it = await getAll("item");
            const ut = await getAll("unit");
            const sessionInv = ldata.filter((a) => a.SessionID === Number(id) && a.SyncStatus !== SyncStatusMap.done);

            setInvData(sessionInv);
            setSession(sess);
            setItem(it);
            setUnits(ut);

        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const processedData = useMemo(() => {
        const merged = invData.map(inv => {
            const itemDetails = item.find(i => i.ID === inv.ItemID) || {};
            const unitDetails = units.find(u => u.ID === inv.UnitID) || {};

            return {
                ...inv,
                Item: itemDetails,
                Unit: unitDetails
            };
        });

        const normalizedSearch = search.trim().toLowerCase();
        if (!normalizedSearch) return merged;

        return merged.filter(inv => {
            const code = inv.Item?.Code?.toLowerCase() || '';
            const description = inv.Item?.Description?.toLowerCase() || '';
            const lot = inv.Lot?.toLowerCase() || '';

            return code.includes(normalizedSearch) ||
                description.includes(normalizedSearch) ||
                lot.includes(normalizedSearch);
        });
    }, [invData, item, units, search]);

    useEffect(() => {
        setData(processedData);
    }, [processedData]);

    const handleDelete = async (id = null) => {
        if (session?.Status !== statusMapping.inprogress || isSync) return;

        if (id) {
            await deleteRecord('SessionInventory', Number(id));
        } else {
            const toDelete = Object.keys(checkedRows).filter(key => checkedRows[key]);

            // Delete all matching records in parallel
            await Promise.all(
                toDelete.map(ID =>
                    deleteRecord('SessionInventory', Number(ID))
                )
            );
        }

        fetchData();
        setCheckedRows({});
    };

    const handleBatchUpdate = async (field, value) => {
        if (session?.Status !== statusMapping.inprogress || isSync) return;

        const toUpdate = Object.keys(checkedRows).filter(key => checkedRows[key]);

        await Promise.all(
            toUpdate.map(async (ID) => {
                const inventory = await getRecordById("SessionInventory", Number(ID));
                if (!inventory) return;
                const updatedRow = {
                    ...inventory,
                    [field]: value,
                    SyncStatus: inventory?.SyncStatus === SyncStatusMap.create ? SyncStatusMap.create : SyncStatusMap.update
                };
                return await updateRecord("SessionInventory", updatedRow);
            })
        );

        fetchData();
    };

    const handleBatchInputNumberChange = async (event, field) => {
        if (session?.Status !== statusMapping.inprogress || isSync) return;

        const value = event.target.value;

        if (/^\d*$/.test(value)) {
            const parsedQty = value === "" ? "" : Math.max(0, parseInt(value, 10));
            setBatchValues(prev => ({
                ...prev,
                [field]: parsedQty === "" ? 0 : parsedQty
            }));
        }
    };

    const handleBatchInputTextChange = async (field, value) => {
        if (session?.Status !== statusMapping.inprogress || isSync) return;

        setBatchValues(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleInputChange = async (row, event, field) => {
        if (session?.Status !== statusMapping.inprogress || isSync) return;

        const value = event.target.value;

        if (/^\d*$/.test(value)) {
            const parsedQty = value === "" ? "" : Math.max(0, parseInt(value, 10));

            const getItem = await getRecordById('SessionInventory', row.ID)

            const updateItem = {
                ...getItem,
                [field]: parsedQty === "" ? 0 : parsedQty,
                SyncStatus: getItem.SyncStatus === SyncStatusMap.create ? SyncStatusMap.create : SyncStatusMap.update
            }

            await updateRecord('SessionInventory', updateItem);
            setData(prev =>
                prev.map(item =>
                    item.ID === row.ID ? { ...item, [field]: parsedQty === "" ? 0 : parsedQty, SyncStatus: updateItem.SyncStatus } : item
                )
            );
        }
    };

    const handleQuantityChange = async (row, change, field) => {
        if (session?.Status !== statusMapping.inprogress || isSync) return;

        const currentQty = row?.[field] || 0;
        const newQuantity = Math.max(0, currentQty + change);

        const getItem = await getRecordById('SessionInventory', row.ID)

        const updateItem = {
            ...getItem,
            [field]: newQuantity,
            SyncStatus: getItem.SyncStatus === SyncStatusMap.create ? SyncStatusMap.create : SyncStatusMap.update
        }

        await updateRecord('SessionInventory', updateItem);
        setData(prev =>
            prev.map(item =>
                item.ID === row.ID ? { ...item, [field]: newQuantity, SyncStatus: updateItem.SyncStatus } : item
            )
        );
    };

    const handleHeaderCheckboxChange = () => {
        if (session?.Status !== statusMapping.inprogress || isSync) return;

        const newCheckedState = !isAllChecked;
        setIsAllChecked(newCheckedState);

        const updatedCheckedRows = data.reduce((acc, item) => {
            acc[item.ItemID] = newCheckedState;
            return acc;
        }, {});

        setCheckedRows(updatedCheckedRows);
    };

    const handleCheckboxChange = (itemId) => {
        if (session?.Status !== statusMapping.inprogress || isSync) return;

        setCheckedRows((prev) => {
            const updated = { ...prev, [itemId]: !prev[itemId] };
            setIsAllChecked(Object.values(updated).every(Boolean)); // Update header checkbox state
            return updated;
        });
    };

    const handleAddInventoryItem = async (row) => {
        if (session?.Status !== statusMapping.inprogress || isSync) return;

        try {
            const nextID = await getNextId("SessionInventory");
            const newItemInventory = {
                ID: nextID,
                ItemID: row.ItemID,
                UnitID: row.UnitID,
                Minimum: 0,
                StockRoomOnHand: 0,
                ShelfOnHand: 0,
                SessionID: row.SessionID,
                Remarks: "",
                Requested: 0,
                Executed: 0,
                Lot: "",
                Distributor: "",
                ExpiryDate: "",
                Availability: SessionItemAvailability.Carried,
                SyncStatus: SyncStatusMap.create
            }
            await addRecord("SessionInventory", newItemInventory);

            setData(prev => [
                ...prev,
                {
                    ...newItemInventory,
                    Item: row.Item,
                    Unit: row.Unit
                }
            ]);

        } catch (error) {
            console.log(error);
        }
    }

    const handleFieldChange = async (row, field, value) => {
        if (session?.Status !== statusMapping.inprogress || isSync) return;

        const inventory = await getRecordById("SessionInventory", row.ID)
        const updatedRow = {
            ...inventory,
            [field]: value,
            SyncStatus: inventory?.SyncStatus === SyncStatusMap.create ? SyncStatusMap.create : SyncStatusMap.update
        };
        await updateRecord("SessionInventory", updatedRow);

        setData(prevData =>
            prevData.map(item =>
                item.ID === updatedRow.ID ? { ...item, [field]: value, SyncStatus: updatedRow.SyncStatus } : item
            )
        );
    };

    // Helper function to group rows by item ID
    function groupByItemId(data) {
        return data.reduce((groups, row) => {
            const key = row.Item?.ID ?? 'unknown';
            if (!groups[key]) {
                groups[key] = [];
            }
            groups[key].push(row);
            return groups;
        }, {});
    }

    function calculateSubtotals(rows) {
        return rows.reduce(
            (totals, row) => {
                totals.stockRoom += Number(row.StockRoomOnHand ?? 0);
                totals.shelf += Number(row.ShelfOnHand ?? 0);
                totals.onHand += (Number(row.StockRoomOnHand ?? 0) + Number(row.ShelfOnHand ?? 0));
                return totals;
            },
            { stockRoom: 0, shelf: 0, onHand: 0 }
        );
    }

    const formatCamelCase = (value) => {
        if (!value) return '';
        return value.replace(/([a-z])([A-Z])/g, '$1 $2');
    };

    return (
        <div className="opal-inventory mb-2">
            <Accordion defaultActiveKey="0" alwaysOpen>
                <Accordion.Item eventKey="0">
                    <Accordion.Header className="p-0 mb-2">
                        <div className="header-container d-flex flex-column flex-md-row justify-content-between align-items-start w-100">
                            <div className="header-left">
                                <TauriImage localImage={section.icon} className="inv-icon-header me-2" />
                                <span className="header-title">{section?.header.toUpperCase()}</span>

                                <Button
                                    className="btn d-flex align-items-center justify-content-center p-0 ms-4 d-none d-md-inline-flex"
                                    size="sm"
                                    disabled={session?.Status !== statusMapping.inprogress || isSync || loading}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowItemModal(true);
                                    }}
                                >
                                    <TauriImage localImage="/Images/flat-color-icons--plus.svg" className="inv-icon-add me-2" />
                                    <div className="text-nowrap">Add Item</div>
                                </Button>
                            </div>
                            <div className="d-flex align-items-center justify-content-end header-filter d-none d-md-flex">
                                {Object.values(checkedRows).some(checked => checked) && (
                                    <>
                                        <div className="d-flex col-lot">

                                        </div>
                                        <div className="d-flex justify-content-center col-expiry position-relative" onClick={(e) => e.stopPropagation()}>
                                            <DatePicker
                                                selected={batchValues.ExpiryDate}
                                                onChange={date => handleBatchInputTextChange('ExpiryDate', date)}
                                                dateFormat="yyyy/MM"
                                                showMonthYearPicker
                                                showFullMonthYearPicker
                                                showFourColumnMonthYearPicker
                                                className="mx-1"
                                                customInput={<ReadOnlyInput />}
                                            />
                                            <TauriImage
                                                localImage="/Images/garden--duplicate-stroke-16.svg"
                                                className="inv-icon-input ms-1"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleBatchUpdate('ExpiryDate', batchValues.ExpiryDate)
                                                }}
                                            />
                                        </div>
                                        <div className="d-flex justify-content-center col-stock">
                                            <input
                                                type="text"
                                                className="mx-1"
                                                value={batchValues.StockRoomOnHand ?? 0}
                                                onChange={(event) => handleBatchInputNumberChange(event, 'StockRoomOnHand')}
                                                onClick={(e) => { e.stopPropagation(); }} />
                                            <TauriImage
                                                localImage="/Images/garden--duplicate-stroke-16.svg"
                                                className="inv-icon-input ms-1"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleBatchUpdate('StockRoomOnHand', batchValues.StockRoomOnHand)
                                                }}
                                            />
                                        </div>
                                        <div className="d-flex justify-content-center col-shelf">
                                            <input type="text" className="mx-1"
                                                value={batchValues.ShelfOnHand ?? 0}
                                                onChange={(event) => handleBatchInputNumberChange(event, 'ShelfOnHand')}
                                                onClick={(e) => { e.stopPropagation(); }} />
                                            <TauriImage
                                                localImage="/Images/garden--duplicate-stroke-16.svg"
                                                className="inv-icon-input ms-1"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleBatchUpdate('ShelfOnHand', batchValues.ShelfOnHand)
                                                }}
                                            />
                                        </div>
                                    </>
                                )}
                                <div className="col-onhand">
                                    <Button className="btn d-flex align-items-center justify-content-center p-0 m-0"
                                        size="sm"
                                        disabled={loading}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowInventoryItemModal(true);
                                        }}>
                                        <TauriImage localImage="/Images/symbols--search.svg" className="inv-icon-find me-1" color={search ? "#FFBD59" : "#2292E3"} />
                                        <div>Find</div>
                                    </Button>
                                </div>
                            </div>

                            <div className="d-flex align-items-center w-100 mt-2 d-block d-md-none text-value">
                                <TauriImage localImage="/Images/symbols--search.svg" className="inv-icon-find me-1" color={"#2292E3"} />
                                <input
                                    type="text"
                                    className="form-control w-100 text-start px-2"
                                    placeholder="Search"
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <Button
                                    className="btn d-flex align-items-center justify-content-center p-0 m-0 mx-1 w-auto"
                                    size="sm"
                                    disabled={session?.Status !== statusMapping.inprogress || isSync || loading}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowItemModal(true);
                                    }}
                                >
                                    <TauriImage localImage="/Images/flat-color-icons--plus.svg" className="inv-icon-find" />
                                </Button>
                            </div>
                        </div>
                    </Accordion.Header>

                    <Accordion.Body className="py-0 px-0">
                        {loading ? (
                            <TauriLoader2 width="2.5rem" height="2.5rem" borderwidth="0.35rem" color="#1D5D9B" />
                        ) : (
                            <div className="inv-table">
                                <table className="table d-none d-md-table">
                                    <thead className="inv-table-header">
                                        <tr>
                                            <th className="text-center col-delete">
                                                {Object.values(checkedRows).some(checked => checked) && (
                                                    <TauriImage
                                                        localImage="/Images/delete-icon.svg"
                                                        className="inv-icon-delete"
                                                        disabled={session?.Status !== statusMapping.inprogress || isSync || loading}
                                                        onClick={(e) => {
                                                            e.stopPropagation(); // Prevent accordion toggle
                                                            handleDelete(); // Open modal
                                                        }} />
                                                )}
                                            </th>
                                            <th className="col-item">
                                                <div>
                                                    <span>ITEM</span>
                                                    <span className="item-count">{Object.entries(groupByItemId(data)).length} items</span>
                                                </div>
                                            </th>
                                            <th></th>
                                            {/* <th className="text-center col-distributor">DISTRIBUTOR</th> */}
                                            <th className="text-center text-nowrap col-availability">AVAIL</th>
                                            <th className="text-center text-nowrap col-lot">LOT NUMBER</th>
                                            <th className="text-center text-nowrap col-expiry">EXPIRY (YYYY/MM)</th>
                                            <th className="text-center text-nowrap col-stock">STOCK ROOM</th>
                                            <th className="text-center text-nowrap  col-shelf">SHELF</th>
                                            <th className="text-center text-nowrap col-onhand">ON HAND</th>
                                        </tr>
                                    </thead>
                                    <tbody className="inv-table-body">
                                        {Object.entries(groupByItemId(data)).map(([itemId, rows]) => {
                                            const { stockRoom, shelf, onHand } = calculateSubtotals(rows);
                                            const minimum = rows[0]?.Item?.Minimum || 0;
                                            const isBelowMinimum = onHand < minimum;
                                            const subtotalStyle = { color: isBelowMinimum ? '#FF0101' : 'inherit' }
                                            return (
                                                <React.Fragment key={itemId}>
                                                    {rows.map((row, idx) => (
                                                        <tr key={row.ID}>
                                                            <td></td>
                                                            {idx === 0 && (
                                                                <td className="col-item align-top" rowSpan={rows.length}>
                                                                    <div className="d-flex flex-column">
                                                                        <div className="item-code">({row?.Item?.Code})</div>
                                                                        <div className="item-description">{row?.Item?.Description}</div>
                                                                    </div>
                                                                </td>
                                                            )}
                                                            <td className="col-checkbox">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={!!checkedRows[row.ID]}
                                                                    onChange={() => handleCheckboxChange(row.ID)}
                                                                />
                                                            </td>
                                                            <td className="text-center col-availability">
                                                                <Dropdown
                                                                    show={openDropdownRow === row.ID}
                                                                    onToggle={() => setOpenDropdownRow(prev => (prev === row.ID ? null : row.ID))}
                                                                >
                                                                    <Dropdown.Toggle
                                                                        as="button"
                                                                        className="btn btn-light btn-sm w-100"
                                                                        disabled={session?.Status !== statusMapping.inprogress || isSync || loading}
                                                                    >
                                                                        {formatCamelCase(getAvailabilityLabel(row.Availability))}
                                                                    </Dropdown.Toggle>

                                                                    {createPortal(
                                                                        <Dropdown.Menu
                                                                            className="opal-inventory-dropdownmenu"
                                                                            style={{ position: "absolute", zIndex: 1 }}
                                                                        >
                                                                            {availabilityOptions.map(key => (
                                                                                <Dropdown.Item
                                                                                    key={key}
                                                                                    onClick={() => {
                                                                                        handleFieldChange(
                                                                                            row,
                                                                                            'Availability',
                                                                                            SessionItemAvailability[key]
                                                                                        );
                                                                                        setOpenDropdownRow(null);
                                                                                    }}
                                                                                >
                                                                                    {formatCamelCase(key)}
                                                                                </Dropdown.Item>
                                                                            ))}
                                                                        </Dropdown.Menu>,
                                                                        document.body
                                                                    )}
                                                                </Dropdown>
                                                            </td>
                                                            <td className="text-value text-nowrap text-center col-lot">
                                                                <input
                                                                    type="text"
                                                                    className="mx-1"
                                                                    value={row.Lot}
                                                                    onChange={e => handleFieldChange(row, 'Lot', e.target.value)}
                                                                />
                                                            </td>
                                                            <td className="text-value text-nowrap text-center col-expiry position-relative">
                                                                <DatePicker
                                                                    selected={row.ExpiryDate || ""}
                                                                    onChange={date => handleFieldChange(row, 'ExpiryDate', date.toLocaleDateString('en-CA'))}
                                                                    dateFormat="yyyy/MM"
                                                                    showMonthYearPicker
                                                                    showFullMonthYearPicker
                                                                    showFourColumnMonthYearPicker
                                                                    className="mx-1"
                                                                    customInput={<ReadOnlyInput />}
                                                                />
                                                            </td>
                                                            <td className="stock-quantity-cell text-nowrap text-center col-stock">
                                                                <TauriImage
                                                                    localImage={"/Images/uil--plus.svg"}
                                                                    alt="icon"
                                                                    onClick={() => handleQuantityChange(row, 1, 'StockRoomOnHand')}
                                                                    color={"#1D5D9B"}
                                                                />
                                                                <input
                                                                    type="text"
                                                                    className="mx-1"
                                                                    value={row.StockRoomOnHand ?? 0}
                                                                    onChange={(event) => handleInputChange(row, event, 'StockRoomOnHand')}
                                                                />
                                                                <TauriImage
                                                                    localImage={"/Images/uil--minus.svg"}
                                                                    alt="icon"
                                                                    onClick={() => handleQuantityChange(row, -1, 'StockRoomOnHand')}
                                                                    color={"#1D5D9B"}
                                                                />
                                                            </td>
                                                            <td className="onshelf-quantity-cell text-nowrap text-center col-shelf">
                                                                <TauriImage
                                                                    localImage={"/Images/uil--plus.svg"}
                                                                    alt="icon"
                                                                    onClick={() => handleQuantityChange(row, 1, 'ShelfOnHand')}
                                                                    color={"#1D5D9B"}
                                                                />
                                                                <input
                                                                    type="text"
                                                                    className="mx-1"
                                                                    value={row?.ShelfOnHand ?? 0}
                                                                    onChange={(event) => handleInputChange(row, event, 'ShelfOnHand')}
                                                                />
                                                                <TauriImage
                                                                    localImage={"/Images/uil--minus.svg"}
                                                                    alt="icon"
                                                                    onClick={() => handleQuantityChange(row, -1, 'ShelfOnHand')}
                                                                    color={"#1D5D9B"}
                                                                />
                                                            </td>
                                                            <td className="text-center col-onhand" style={{ color: (row.ShelfOnHand ?? 0) + (row.StockRoomOnHand ?? 0) < row.Minimum ? 'red' : 'inherit' }}>
                                                                {((row.ShelfOnHand ?? 0) + (row.StockRoomOnHand ?? 0)).toLocaleString()}
                                                            </td>
                                                        </tr>
                                                    ))}

                                                    {/* Subtotal row */}
                                                    <tr className="subtotal-row">
                                                        <td className="text-center"></td>
                                                        <td className="text-center"></td>
                                                        {/* <td className="text-center"></td> */}
                                                        <td className="text-center"></td>
                                                        <td className="text-center">
                                                            <div className="d-flex justify-content-center">
                                                                <Button
                                                                    className="btn-subtotal d-flex align-items-center"
                                                                    size="sm"
                                                                    onClick={() => handleAddInventoryItem(rows[0])}
                                                                >
                                                                    <TauriImage localImage="/Images/flat-color-icons--plus-primary.svg" className="inv-icon-add me-2" />
                                                                    <div>Add Lot</div>
                                                                </Button>
                                                            </div>
                                                        </td>
                                                        <td className="d-flex justify-content-center">
                                                            <div className="d-flex justify-content-center">
                                                                <Button
                                                                    className="btn-subtotal d-flex align-items-center"
                                                                    size="sm"
                                                                >
                                                                    {/* <TauriImage localImage="/Images/flat-color-icons--plus-v3.svg" className="inv-icon-add me-2" /> */}
                                                                    <div>Inv. Lvl : {minimum}</div>
                                                                </Button>
                                                            </div>
                                                        </td>
                                                        <td className="text-center" style={subtotalStyle}>{stockRoom}</td>
                                                        <td className="text-center" style={subtotalStyle}>{shelf}</td>
                                                        <td className="text-center" style={subtotalStyle}>{onHand}</td>
                                                    </tr>
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                <div className="mobile-view d-md-none">
                                    {Object.entries(groupByItemId(data)).map(([itemId, rows]) => (
                                        rows.map((row) => (
                                            <div key={row.ID} className="inv-card border rounded p-3 mb-3">
                                                <div className="mb-2">
                                                    <div className="d-flex align-items-center justify-content-between">
                                                        <div className="header-title">ITEM:</div>
                                                        <div className="d-flex align-items-center">
                                                            <TauriImage
                                                                localImage="/Images/delete-icon.svg"
                                                                className="inv-icon-delete"
                                                                disabled={session?.Status !== statusMapping.inprogress || isSync || loading}
                                                                onClick={(e) => {
                                                                    handleDelete(row?.ID); // Open modal
                                                                }} />
                                                            <Button
                                                                className="btn-subtotal d-flex align-items-center"
                                                                size="sm"
                                                                onClick={() => handleAddInventoryItem(row)}
                                                            >
                                                                <TauriImage localImage="/Images/flat-color-icons--plus-primary.svg" className="inv-icon-add me-2" />
                                                                <div>Add Lot</div>
                                                            </Button>
                                                        </div>
                                                    </div>
                                                    <div className="item-code">({row?.Item?.Code})</div>
                                                    <div className="item-description">{row?.Item?.Description}</div>
                                                </div>
                                                <div className="mb-2 text-value">
                                                    <div className="header-title">AVAIL:</div>
                                                    <Dropdown
                                                        show={openDropdownRow === row.ID}
                                                        onToggle={isOpen => setOpenDropdownRow(isOpen ? row.ID : null)}
                                                    >
                                                        <Dropdown.Toggle
                                                            as="button"
                                                            className="btn btn-light btn-sm w-100"
                                                            disabled={session?.Status !== statusMapping.inprogress || isSync || loading}
                                                        >
                                                            {formatCamelCase(getAvailabilityLabel(row.Availability))}
                                                        </Dropdown.Toggle>

                                                        {createPortal(
                                                            <Dropdown.Menu
                                                                className="opal-inventory-mobile-dropdownmenu "
                                                                style={{ position: "absolute", zIndex: 1 }}
                                                            >
                                                                {availabilityOptions.map(key => (
                                                                    <Dropdown.Item
                                                                        key={key}
                                                                        onClick={() => {
                                                                            handleFieldChange(
                                                                                row,
                                                                                'Availability',
                                                                                SessionItemAvailability[key]
                                                                            );
                                                                            setOpenDropdownRow(null);
                                                                        }}
                                                                    >
                                                                        {formatCamelCase(key)}
                                                                    </Dropdown.Item>
                                                                ))}
                                                            </Dropdown.Menu>,
                                                            document.body
                                                        )}
                                                    </Dropdown>
                                                </div>
                                                <div className="mb-2 text-value">
                                                    <div className="header-title">LOT:</div>
                                                    <input
                                                        type="text"
                                                        value={row.Lot}
                                                        onChange={e => handleFieldChange(row, 'Lot', e.target.value)}
                                                    />
                                                </div>
                                                <div className="mb-2 expiry text-value">
                                                    <div className="header-title">EXPIRY:</div>
                                                    <DatePicker
                                                        selected={row.ExpiryDate || ""}
                                                        onChange={date => handleFieldChange(row, 'ExpiryDate', date.toLocaleDateString('en-CA'))}
                                                        dateFormat="yyyy/MM"
                                                        showMonthYearPicker
                                                        showFullMonthYearPicker
                                                        showFourColumnMonthYearPicker
                                                        className="mx-1 "
                                                        customInput={<ReadOnlyInput />}
                                                    />
                                                </div>
                                                <div className="mb-2 stock-quantity-cell text-nowrap">
                                                    <div className="header-title">STOCK ROOM:</div>
                                                    <div>
                                                        <TauriImage
                                                            localImage={"/Images/uil--plus.svg"}
                                                            alt="icon"
                                                            onClick={() => handleQuantityChange(row, 1, 'StockRoomOnHand')}
                                                            color={"#1D5D9B"}
                                                        />
                                                        <input
                                                            type="text"
                                                            className="mx-1"
                                                            value={row.StockRoomOnHand ?? 0}
                                                            onChange={(event) => handleInputChange(row, event, 'StockRoomOnHand')}
                                                        />
                                                        <TauriImage
                                                            localImage={"/Images/uil--minus.svg"}
                                                            alt="icon"
                                                            onClick={() => handleQuantityChange(row, -1, 'StockRoomOnHand')}
                                                            color={"#1D5D9B"}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="mb-2 onshelf-quantity-cell text-nowrap">
                                                    <div className="header-title">SHELF:</div>
                                                    <div>
                                                        <TauriImage
                                                            localImage={"/Images/uil--plus.svg"}
                                                            alt="icon"
                                                            onClick={() => handleQuantityChange(row, 1, 'ShelfOnHand')}
                                                            color={"#1D5D9B"}
                                                        />
                                                        <input
                                                            type="text"
                                                            className="mx-1"
                                                            value={row?.ShelfOnHand ?? 0}
                                                            onChange={(event) => handleInputChange(row, event, 'ShelfOnHand')}
                                                        />
                                                        <TauriImage
                                                            localImage={"/Images/uil--minus.svg"}
                                                            alt="icon"
                                                            onClick={() => handleQuantityChange(row, -1, 'ShelfOnHand')}
                                                            color={"#1D5D9B"}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="mb-2">
                                                    <div className="header-title">ON HAND:</div>
                                                    <div className="text-center">
                                                        {(row.ShelfOnHand ?? 0) + (row.StockRoomOnHand ?? 0)}
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ))}
                                </div>
                            </div>

                        )}
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
            <OpalAddItemModal
                show={showItemModal}
                onHide={() => setShowItemModal(false)}
                data={item}
                onAddSelected={handleAddSelected}
            />
            <OpalInventoryFindModal
                show={showInventoryItemModal}
                onHide={() => setShowInventoryItemModal(false)}
                search={search}
                setSearch={setSearch}
            />
            <OpalAlertModal
                show={showValidation}
                onHide={() => {
                    setShowValidation(false);
                }}
                title={"Message"}
                bodyText={validationMessage}
                modalType="message"
            />
        </div>
    );
};

export default OpalInventory;
